import { v4 as uuidv4 } from 'uuid'
import Dexie, { EntityTable } from 'dexie'
import { isWindowDefined } from '@chegg/utils'
import { JSONContent } from '@tiptap/core'
import { Feedback, PaperStatus } from '@chegg-wt/core/types/paraphraser'
import { SentenceOffset, ToastType } from '@chegg-wt/core/state/paraphraser/types'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { notifyToast } from '@chegg-wt/core/state/paraphraser/actions'
import { store } from '@chegg-wt/core/state'
import { getSessionStorageItem } from '../sessionStorage'
import {
  transformSentencesToSaveInDb,
  transformSentencesForUi,
  sanitizeFeedbackList,
} from './helpers'

export const INDEXED_DB_NAME = 'ParaphraserDatabase'

export type PaperDBType = {
  id: string
  title: string
  created_at: string
  paperContent: {
    editorJSON: JSONContent
  }
  feedbackState: {
    feedbackList: Feedback[]
    sentences: Omit<SentenceOffset, 'originalText' | 'lastUpdated'>[]
    totalFeedbackCount: number
  }
  requiresRecheck: boolean
  status: PaperStatus
  updated_at?: string
}

interface ParaphraseDbType extends Dexie {
  papers: EntityTable<
    PaperDBType,
    'id' // primary key "id" (for the typings only)
  >
}

const getIndexedDb = () => {
  let paperDb: ParaphraseDbType
  return () => {
    if (isWindowDefined) {
      if (!paperDb) {
        paperDb = new Dexie(INDEXED_DB_NAME) as ParaphraseDbType
        // Define the structure of your database
        paperDb.version(1).stores({
          papers: '&id, title, created_at', // primary key "id" (for the runtime!)
        })
      }
      return paperDb
    }
    return null
  }
}

const getPaperIndexedDb = getIndexedDb()

export default getPaperIndexedDb

export const clearPapersForIndexDb = async () => {
  const paraphraserIndexDb = getPaperIndexedDb()
  if (paraphraserIndexDb) {
    await paraphraserIndexDb.papers.clear()
    console.log('Papers store cleared.')
  }
}

/**
 * ------------------------------------------------------
 * Utility functions to create a paper
 * ------------------------------------------------------
 */
export const createPaper = async (
  title: string,
  paperContent: PaperDBType['paperContent'],
  feedbackState: PaperDBType['feedbackState'],
  isLoggedIn?: boolean,
  id?: string,
  status?: PaperStatus
) => {
  const isMultiPaperEnabled = getSessionStorageItem('isMultiPaperEnabled') === 'true'
  const paraphraserIndexDb = getPaperIndexedDb()
  let paperData: any = {
    id: id || uuidv4(),
    title,
    created_at: new Date().toISOString(),
    paperContent,
    feedbackState,
    requiresRecheck: true,
    status: status || PaperStatus.DRAFT,
  }

  if (id) {
    paperData = await paraphraserIndexDb.papers.get(paperData.id)
  }

  try {
    let newPaperId
    if (isLoggedIn && isMultiPaperEnabled) {
      const req = {
        title: paperData.title,
        paperContent: JSON.stringify(paperData.paperContent),
        createdAt: paperData.created_at,
        paperId: paperData.id,
        feedbackState: {
          feedbackList: paperData.feedbackState.feedbackList,
          sentences: paperData.feedbackState.sentences,
          initialFeedbackCount: paperData.feedbackState.totalFeedbackCount,
        },
        requiresReCheck: paperData.requiresRecheck,
        status,
      }
      const response = await oneGraph.createProofReaderPaper({
        request: req,
      })
      newPaperId = response.paperId
      console.log(`Transfer Of Ownership completed with id: ${newPaperId}`)
    } else {
      newPaperId = await paraphraserIndexDb.papers.add(paperData)
    }
    if (!newPaperId) {
      throw new Error(`createProofReaderPaper failed`)
    }

    // TODO: after error handling try to dispatch all the toasts from components/state.
    if (newPaperId && isMultiPaperEnabled)
      store.dispatch(notifyToast(ToastType.SAVING_PAPER))
    return newPaperId
  } catch (error) {
    // store.dispatch(notifyToast(ToastType.SAVING_PAPER_ERROR))
    console.error(`CreatePaper failed `, error)
  }
}

/**
 * ----------------------------------------------------
 * Utility functions to read a paper in local storage
 * ----------------------------------------------------
 */
export const readPaper = async (paperId, isLoggedIn): Promise<PaperDBType> => {
  // TODO: create common functions, optimize code for better readability.
  const isMultiPaperEnabled = getSessionStorageItem('isMultiPaperEnabled') === 'true'
  try {
    let paper
    if (isLoggedIn && isMultiPaperEnabled) {
      const response = await oneGraph.getPaper({
        paperId,
      })
      paper = {
        id: paperId,
        title: response.title,
        paperContent: JSON.parse(response.paperContent),
        feedbackState: {
          feedbackList: response.feedbackState.feedbackList,
          sentences: response.feedbackState.sentences,
          totalFeedbackCount: response.feedbackState.initialFeedbackCount,
        },
        requiresRecheck: response.requiresReCheck,
        created_at: response.createdAt,
      }
    } else {
      const paraphraserIndexDb = getPaperIndexedDb()
      paper = await paraphraserIndexDb.papers.get(paperId)
    }

    if (!paper) {
      throw new Error(`Couldn't find Paper ${paperId}.`)
    }

    const updatedFeedbackState =
      paper.feedbackState.sentences.length > 0
        ? {
            ...paper.feedbackState,
            sentences: transformSentencesForUi(paper.feedbackState.sentences),
          }
        : paper.feedbackState

    const updatedPaper = { ...paper, feedbackState: updatedFeedbackState }

    console.log(`Paper read for id ${paperId}`, updatedPaper)

    return updatedPaper
  } catch (error) {
    // if (isLoggedIn) store.dispatch(notifyToast(ToastType.SAVING_PAPER_ERROR))
    console.error(error)
  }
}

/**
 * ----------------------------------------------------
 * Utility functions to update a paper in local storage
 * ----------------------------------------------------
 */
export const updatePaper = async (
  paperId: string,
  data: {
    status?: PaperStatus
    title?: string
    paperContent?: PaperDBType['paperContent']
    feedbackState?: PaperDBType['feedbackState']
    requiresRecheck?: boolean
  },
  isLoggedIn?: boolean
) => {
  const isMultiPaperEnabled = getSessionStorageItem('isMultiPaperEnabled') === 'true'
  try {
    const {
      status = PaperStatus.DRAFT,
      title,
      paperContent,
      feedbackState = { feedbackList: [], sentences: [], totalFeedbackCount: 0 },
      requiresRecheck,
    } = data
    const updatedAt = new Date().toISOString()
    const updatedFeedbackState: any =
      feedbackState.sentences && feedbackState.sentences.length > 0
        ? {
            ...feedbackState,
            sentences: transformSentencesToSaveInDb(feedbackState.sentences),
          }
        : feedbackState

    let isUpdated
    if (isLoggedIn && isMultiPaperEnabled) {
      const sanitizedFeedbackList = updatedFeedbackState.feedbackList
        ? sanitizeFeedbackList(updatedFeedbackState.feedbackList)
        : []

      const { isSuccess } = await oneGraph.updateProofReaderPaper({
        request: {
          paperId,
          ...(title && { title }),
          ...(paperContent && { paperContent: JSON.stringify(paperContent) }),
          ...(updatedFeedbackState && {
            feedbackState: {
              feedbackList: sanitizedFeedbackList,
              sentences: updatedFeedbackState.sentences || [],
              initialFeedbackCount: updatedFeedbackState.totalFeedbackCount || 0,
            },
          }),
          ...(requiresRecheck != null && { requiresReCheck: requiresRecheck }),
          status,
        },
      })
      isUpdated = isSuccess
    } else {
      const paraphraserIndexDb = getPaperIndexedDb()
      isUpdated = await paraphraserIndexDb.papers.update(paperId, {
        ...(status && { status }),
        ...(title && { title }),
        ...(paperContent && { paperContent }),
        ...(feedbackState && { feedbackState: updatedFeedbackState }),
        ...(requiresRecheck != null && { requiresRecheck }),
        updated_at: updatedAt,
      })
    }

    if (!isUpdated) {
      throw new Error(`Paper update failed for id ${paperId}`)
    }
    if (isUpdated && isMultiPaperEnabled)
      store.dispatch(notifyToast(ToastType.SAVING_PAPER))
  } catch (error) {
    // store.dispatch(notifyToast(ToastType.SAVING_PAPER_ERROR))
    console.log(`Paper update failed for id ${paperId}`)
  }
}

/**
 * ----------------------------------------------------
 * Utility functions to delete a paper from local storage
 * ----------------------------------------------------
 */
export const deletePaper = (paperId) => {
  try {
    const paraphraserIndexDb = getPaperIndexedDb()
    paraphraserIndexDb.papers.delete(paperId)
  } catch (error) {
    console.log(`Paper update failed for id ${paperId}`)
  }
}

/**
 * Returns First record from papers table in IndexDB,
 * @returns First Paper in IndexDB, if exists, else null
 */
export const getFirstPaper = async (): Promise<PaperDBType> => {
  const paraphraserIndexDb = getPaperIndexedDb()
  const papers = await paraphraserIndexDb.papers.limit(1).toArray()
  if (papers && papers.length > 0) {
    const [paper] = papers
    const updatedFeedbackState =
      paper.feedbackState.sentences.length > 0
        ? {
            ...paper.feedbackState,
            sentences: transformSentencesForUi(paper.feedbackState.sentences),
          }
        : paper.feedbackState
    return {
      ...paper,
      feedbackState: updatedFeedbackState,
    }
  }
  return null
}
